import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './SearchWidget.css'

/**
 * SEPARATELY LOCKED — beads memory locked-2026-07-18-nexexec-company-dashboard-search.
 * Canonical snapshot: dashboard/components/search-widget.LOCKED.html.
 * Structure and styling are frozen: the describe field with mic, then Upload JD,
 * Filters and Search Executives, three chips, a 2px teal divider, then the
 * popular-search flag pills — all inside the tinted teal panel.
 */

/** Flag emoji stay with the pill data — they are content, not decoration. */
const POPULAR = [
  { flag: '🇺🇸', role: 'CEO', where: 'USA' },
  { flag: '🇺🇸', role: 'CTO', where: 'USA' },
  { flag: '🇬🇧', role: 'VP of Engineering', where: 'London', plain: true },
  { flag: '🇨🇳', role: 'CFO', where: 'China' },
  { flag: '🇪🇺', role: 'COO', where: 'Europe' },
]

const CHIPS = [
  { label: 'Paste Role Description', to: '/search', path: <><rect x="5" y="3" width="10" height="14" rx="2" /><path d="M7.5 7h5M7.5 10h5M7.5 13h3" /></> },
  { label: 'Create Role Pipeline', to: '/pipelines', path: <><rect x="4" y="4" width="12" height="12" rx="2" /><path d="M10 7.5v5M7.5 10h5" /></> },
  { label: 'View Saved Searches', to: '/saved-searches', path: <><circle cx="9" cy="9" r="5" /><path d="m12.8 12.8 3.4 3.4" /></> },
]

export function SearchWidget() {
  const [query, setQuery] = useState('')
  const navigate = useNavigate()

  /**
   * No search endpoint exists yet, so this routes to the Search destination
   * carrying the query rather than pretending to return results. Navigation
   * only — the structure of this widget is locked.
   */
  const goSearch = (q: string) =>
    navigate(q.trim() ? `/search?q=${encodeURIComponent(q.trim())}` : '/search')

  return (
    <section className="sec srchsec">
      <div className="sb2">
        <h2 className="sb2-h">Start With the Executive You Need</h2>
        <p className="sb2-p">
          Search manually, speak you criteria, or upload a job description. NexExec helps convert
          your input into structured executive hiring criteria.
        </p>

        <form
          className="sb2-row"
          onSubmit={(e) => {
            e.preventDefault()
            goSearch(query)
          }}
        >
          <div className="sb2-field">
            <label className="sr-only" htmlFor="describe">Describe the executive you need</label>
            <input
              id="describe" type="search" value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Describe the executive you need — role, industry, stage, region, or leadership challenge…"
            />
            <button className="sb2-mic" type="button" aria-label="Search by voice">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <rect x="7.6" y="2.4" width="4.8" height="9" rx="2.4" />
                <path d="M4.6 9.3a.9.9 0 0 1 1.8 0 3.6 3.6 0 0 0 7.2 0 .9.9 0 0 1 1.8 0 5.4 5.4 0 0 1-4.5 5.3v1.6h1.8a.9.9 0 0 1 0 1.8H8.1a.9.9 0 0 1 0-1.8h1.8v-1.6a5.4 5.4 0 0 1-4.5-5.3z" />
              </svg>
            </button>
          </div>
          <button className="obtn" type="button" onClick={() => goSearch(query)}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M10 12.5V4M6.8 7.2 10 4l3.2 3.2M4 13.5V16h12v-2.5" />
            </svg>
            Upload JD
          </button>
          <button className="obtn" type="button" onClick={() => goSearch(query)}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M3.5 5.5h13M6 10h8M8.5 14.5h3" />
            </svg>
            Filters
          </button>
          <button className="fbtn" type="submit">Search Executives</button>
        </form>

        <div className="sb2-chips">
          {CHIPS.map((chip) => (
            <button className="obtn" type="button" key={chip.label} onClick={() => navigate(chip.to)}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                {chip.path}
              </svg>
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      <div className="pop">
        <div className="pop-h">
          <span className="flame" aria-hidden="true">🔥</span>
          <span className="pop-t">Popular Current Market Searches</span>
          <span className="i" tabIndex={0} role="button" aria-label="More information">i</span>
          <span className="rule" />
        </div>
        <div className="pills">
          {POPULAR.map((pill) => (
            <button
              className="p" type="button" key={`${pill.role}-${pill.where}`}
              onClick={() => goSearch(`${pill.role} ${pill.where}`)}
            >
              <span className="fg" aria-hidden="true">{pill.flag}</span>
              {pill.plain ? `${pill.role} – ${pill.where}` : <><b>{pill.role}</b> – {pill.where}</>}
              <span className="cv" aria-hidden="true">▾</span>
            </button>
          ))}
          <button className="p more" type="button" onClick={() => navigate('/search')}>+ More Searches ›</button>
        </div>
      </div>
    </section>
  )
}
